import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Flex,
  Typography,
  Row,
  Col,
  Card,
  Input,
  Button,
  Select,
  AutoComplete,
  Spin,
  Tooltip,
} from 'antd';
import {
  ReloadOutlined,
  BookOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useParams } from 'react-router-dom';
import { useTranslation } from '../../i18n/TranslationContext';
import DashboardService from '../../service';

const AUCTION_NAMES = {
  1: 'IAAI US',
  2: 'Copart',
  3: 'Manheim',
  5: 'ACV',
  11: 'IAAI Canada'
};

function mapUserToWatchlistFilters(user) {
  if (!user) return { user_id: '', user_subbider_id: '' };
  const uid = user.user_id || '';
  if (user.user_subbider_id) {
    return { user_id: uid, user_subbider_id: user.user_subbider_id };
  }
  if (uid.includes('.')) {
    const [x, y] = uid.split('.');
    return { user_id: x, user_subbider_id: y || '' };
  }
  return { user_id: uid, user_subbider_id: uid };
}

function getLotUrl(item) {
  const auction = Number(item.auction);
  const lotNumber = String(item.lot_number || '').trim();
  const vehicleId = String(item.vehicle_id || item.lot_number || '').trim();
  const id = vehicleId || lotNumber;
  if (!id) return null;
  switch (auction) {
    case 5: return `https://app.acvauctions.com/marketplace/${lotNumber}?was_recommendation=false`;
    case 2: return `https://www.copart.com/lot/${lotNumber}/`;
    case 1: return `https://www.iaai.com/VehicleDetail/${id}~US`;
    case 11: return `https://www.iaai.com/VehicleDetail/${id}~CA`;
    case 3: return `https://search.manheim.com/`;
    default: return null;
  }
}

const PLACEHOLDER_IMG = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="140"%3E%3Crect fill="%23e0e0e0" width="200" height="140"/%3E%3Ctext x="50%25" y="50%25" fill="%23999" text-anchor="middle" dy=".3em" font-size="14"%3ENo image%3C/text%3E%3C/svg%3E';

const WatchlistPage = () => {
  const { user } = useAuth();
  // eslint-disable-next-line no-unused-vars -- lang reserved for i18n routing
  const { lang } = useParams();
  const { t } = useTranslation();
  const isSuperAdmin = user?.role === 'super_admin';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    user_id: '',
    user_subbider_id: '',
    auction: '',
    lot_number: '',
    model: ''
  });
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const [selectedUserFilter, setSelectedUserFilter] = useState(null);
  const [userFilterResults, setUserFilterResults] = useState([]);
  const [userFilterLoading, setUserFilterLoading] = useState(false);
  const [userFilterWaiting, setUserFilterWaiting] = useState(false);
  const userFilterDebounceRef = useRef();
  const [userSearchValue, setUserSearchValue] = useState('');

  const handleUserFilterSearch = useCallback(async (searchTerm, reason) => {
    if (reason === 'reset' || reason === 'clear') {
      if (userFilterDebounceRef.current) {
        clearTimeout(userFilterDebounceRef.current);
        userFilterDebounceRef.current = null;
      }
      setUserFilterResults([]);
      setUserFilterLoading(false);
      setUserFilterWaiting(false);
      return;
    }
    if (userFilterDebounceRef.current) {
      clearTimeout(userFilterDebounceRef.current);
      userFilterDebounceRef.current = null;
    }
    if (!searchTerm || searchTerm.trim().length < 2) {
      setUserFilterResults([]);
      setUserFilterLoading(false);
      setUserFilterWaiting(false);
      return;
    }
    setUserFilterWaiting(true);
    userFilterDebounceRef.current = setTimeout(async () => {
      userFilterDebounceRef.current = null;
      try {
        setUserFilterWaiting(false);
        setUserFilterLoading(true);
        const response = await DashboardService.searchUsers({
          quickSearch: searchTerm.trim(),
          page: 1,
          limit: 50
        });
        setUserFilterResults(response?.users || []);
      } catch (err) {
        console.error('Error searching users:', err);
        setUserFilterResults([]);
      } finally {
        setUserFilterLoading(false);
      }
    }, 800);
  }, []);

  useEffect(() => {
    return () => {
      if (userFilterDebounceRef.current) {
        clearTimeout(userFilterDebounceRef.current);
      }
    };
  }, []);

  const loadWatchlist = useCallback(async (filterOverrides) => {
    setLoading(true);
    setError('');
    try {
      let data;
      if (isSuperAdmin) {
        const activeFilters = filterOverrides ?? filtersRef.current;
        const cleaned = Object.fromEntries(
          Object.entries(activeFilters).filter(([, v]) => v !== undefined && v !== null && v !== '')
        );
        data = await DashboardService.getWatchlistAdmin(cleaned);
      } else {
        data = await DashboardService.getMyWatchlist();
      }
      const list = data?.data || [];
      setItems(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading watchlist:', err);
      setError(err.message || 'Failed to load watchlist');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    loadWatchlist();
  }, [loadWatchlist]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    loadWatchlist(filtersRef.current);
  };

  const handleRemove = async (item) => {
    try {
      const payload = item.id
        ? { id: item.id }
        : {
            user_subbider_id: item.user_subbider_id,
            lot_number: item.lot_number || item.vehicle_id,
            vehicle_id: item.vehicle_id,
          };
      await DashboardService.removeFromWatchlist(payload);
      loadWatchlist();
    } catch (err) {
      console.error('Error removing from watchlist:', err);
      setError(err.message || 'Failed to remove');
    }
  };

  const handleCardClick = (item) => {
    const url = getLotUrl(item);
    if (url) window.open(url, '_blank');
  };

  const userOptions = userFilterResults.map((u) => ({
    value: u.user_id || u.user_name,
    label: (
      <div>
        <div style={{ fontWeight: 500 }}>{u.name || u.user_name || 'Unknown'}</div>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {u.user_name && `Username: ${u.user_name}`}
          {u.user_name && u.email && ' • '}
          {u.email && `Email: ${u.email}`}
          {(u.user_name || u.email) && u.user_id && ' • '}
          {u.user_id && `ID: ${u.user_id}`}
        </Typography.Text>
      </div>
    ),
    user: u
  }));

  return (
    <div style={{ padding: '12px 24px' }}>
      <Flex align="center" justify="space-between" wrap="wrap" gap={8} style={{ marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <BookOutlined style={{ color: 'var(--colorPrimary)' }} />
          {t('nav.watchlist', 'Watchlist')}
        </Typography.Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={loadWatchlist}
          disabled={loading}
        >
          {t('common.refresh', 'Refresh')}
        </Button>
      </Flex>

      {isSuperAdmin && (
        <Card style={{ marginBottom: 16 }}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {t('common.search', 'Search')} / {t('common.filters', 'Filters')}
          </Typography.Text>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8} lg={6}>
              <AutoComplete
                style={{ width: '100%' }}
                size="small"
                options={userOptions}
                filterOption={false}
                value={userSearchValue}
                onSelect={(value, option) => {
                  const u = option?.user;
                  setSelectedUserFilter(u);
                  setUserSearchValue(u?.name || u?.user_name || '');
                  const { user_id } = mapUserToWatchlistFilters(u);
                  setFilters(prev => ({ ...prev, user_id }));
                }}
                onSearch={(val) => {
                  setUserSearchValue(val);
                  handleUserFilterSearch(val, val ? 'input' : 'clear');
                }}
                onClear={() => {
                  setUserSearchValue('');
                  setSelectedUserFilter(null);
                  setFilters(prev => ({ ...prev, user_id: '' }));
                  handleUserFilterSearch('', 'clear');
                }}
                placeholder="Filter by User - Type username to search..."
                notFoundContent={userFilterLoading || userFilterWaiting ? <Spin size="small" /> : 'Type at least 2 characters to search'}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                size="small"
                style={{ width: '100%' }}
                placeholder="Auction"
                value={filters.auction || undefined}
                onChange={(v) => handleFilterChange('auction', v ?? '')}
                allowClear
                options={[
                  { value: '', label: 'All' },
                  ...Object.entries(AUCTION_NAMES).map(([id, name]) => ({ value: id, label: name }))
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Input
                size="small"
                placeholder="Lot Number"
                value={filters.lot_number}
                onChange={(e) => handleFilterChange('lot_number', e.target.value)}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Input
                size="small"
                placeholder="Model"
                value={filters.model}
                onChange={(e) => handleFilterChange('model', e.target.value)}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Button type="primary" onClick={handleSearch} block>
                {t('common.search', 'Search')}
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      {error && (
        <Typography.Text type="danger" style={{ display: 'block', marginBottom: 16 }}>{error}</Typography.Text>
      )}

      {loading ? (
        <Flex justify="center" style={{ padding: 48 }}>
          <Spin size="large" />
        </Flex>
      ) : items.length === 0 ? (
        <Card>
          <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: 32 }}>
            {t('watchlist.notFound', 'not found')}
          </Typography.Text>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {items.map((item) => {
            const lotUrl = getLotUrl(item);
            return (
              <Col xs={24} sm={12} md={8} lg={6} key={item._id || item.id || `${item.user_id}-${item.lot_number}-${item.auction}`}>
                <Card
                  hoverable
                  style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}
                  styles={{ body: { padding: 0, flex: 1, display: 'flex', flexDirection: 'column' } }}
                  cover={
                    <div
                      style={{
                        height: 140,
                        overflow: 'hidden',
                        cursor: lotUrl ? 'pointer' : 'default',
                        background: '#f0f0f0'
                      }}
                      onClick={() => lotUrl && handleCardClick(item)}
                    >
                      <img
                        alt={item.model || 'Vehicle'}
                        src={item.image_link || PLACEHOLDER_IMG}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.src = PLACEHOLDER_IMG;
                        }}
                      />
                    </div>
                  }
                >
                  <Tooltip title={t('watchlist.remove', 'Remove from watchlist')}>
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={(e) => { e.stopPropagation(); handleRemove(item); }}
                      style={{ position: 'absolute', top: 8, right: 8, zIndex: 1, background: 'rgba(255,255,255,0.9)' }}
                    />
                  </Tooltip>
                  <div
                    style={{ padding: 12, flexGrow: 1, cursor: lotUrl ? 'pointer' : 'default' }}
                    onClick={() => lotUrl && handleCardClick(item)}
                  >
                    <Typography.Text strong ellipsis style={{ display: 'block' }}>
                      {item.model || '—'}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                      Lot: {item.lot_number || item.vehicle_id || '—'}
                    </Typography.Text>
                    {item.year && (
                      <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                        Year: {item.year}
                      </Typography.Text>
                    )}
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      {AUCTION_NAMES[item.auction] || `Auction ${item.auction}`}
                      {isSuperAdmin && item.user_id && ` • ${item.user_id}`}
                    </Typography.Text>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
};

export default WatchlistPage;
